import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ElementRef } from '@angular/core';

@Component({
  selector: 'acs-modal',
  templateUrl: './acs-modal.html',
  styleUrls: ['./acs-modal.scss']
})
export class AcsModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() title: string = '';
  @Input() size: 'small' | 'medium' | 'large' | 'fullscreen' = 'medium';
  @Input() showCloseButton: boolean = true;
  @Input() closeOnBackdropClick: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() preventBodyScroll: boolean = true;
  @Input() hasFooterContent: boolean = false;

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() backdropClick = new EventEmitter<void>();

  private previouslyFocusedElement: HTMLElement | null = null;
  private keydownListener?: (event: KeyboardEvent) => void;

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    if (this.isOpen) {
      this.handleOpen();
    }
  }

  ngOnDestroy(): void {
    this.handleClose();
  }

  ngOnChanges(): void {
    if (this.isOpen) {
      this.handleOpen();
    } else {
      this.handleClose();
    }
  }

  open(): void {
    this.isOpen = true;
    this.handleOpen();
  }

  close(): void {
    this.isOpen = false;
    this.handleClose();
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.backdropClick.emit();
      if (this.closeOnBackdropClick) {
        this.close();
      }
    }
  }

  private handleOpen(): void {
    if (typeof document !== 'undefined') {
      // Store currently focused element
      this.previouslyFocusedElement = document.activeElement as HTMLElement;
      
      // Prevent body scroll
      if (this.preventBodyScroll) {
        document.body.style.overflow = 'hidden';
      }

      // Set up escape key listener
      if (this.closeOnEscape) {
        this.keydownListener = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            this.close();
          }
        };
        document.addEventListener('keydown', this.keydownListener);
      }

      // Focus trap - focus first focusable element in modal
      setTimeout(() => {
        const focusableElement = this.elementRef.nativeElement.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElement) {
          focusableElement.focus();
        }
      }, 0);

      this.opened.emit();
    }
  }

  private handleClose(): void {
    if (typeof document !== 'undefined') {
      // Restore body scroll
      if (this.preventBodyScroll) {
        document.body.style.overflow = '';
      }

      // Remove escape key listener
      if (this.keydownListener) {
        document.removeEventListener('keydown', this.keydownListener);
        this.keydownListener = undefined;
      }

      // Restore focus to previously focused element
      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
        this.previouslyFocusedElement = null;
      }
    }
  }
}
